/**
 * Derived reading metrics. The SAME formulas as the server's (answer-engagement contract section 3);
 * these thresholds are heuristics to tune, and any change here must be mirrored there.
 * Inputs are timings and counts only; no answer text ever reaches this module.
 */

export interface SectionStat {
  i: number
  words: number
  visible_ms: number
  visits: number
}

/** Words per minute for a careful reader, and the legal/technical text factor. */
export const WORDS_PER_MIN = 220
export const TEXT_FACTOR = 1.3
export const MIN_SECTION_MS = 800
export const SECTION_READ_SHARE = 0.3

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

export const expectedMs = (answerWords: number): number => (answerWords / WORDS_PER_MIN) * 60000 * TEXT_FACTOR

export function readMs(sections: SectionStat[], dwellMs: number): number {
  return Math.min(dwellMs, sections.reduce((a, s) => a + s.visible_ms, 0))
}

/** read_ms / expected_ms; null when the answer is too short (< 30 words) to judge. */
export function readingEffort(sections: SectionStat[], dwellMs: number, answerWords: number): number | null {
  if (answerWords < 30) return null
  const exp = expectedMs(answerWords)
  return exp > 0 ? readMs(sections, dwellMs) / exp : null
}

/** Share of the answer's words that sit in sections on screen long enough to have been read. */
export function coverage(sections: SectionStat[], answerWords: number): number {
  if (answerWords <= 0) return 0
  let words = 0
  for (const s of sections) {
    if (s.visible_ms >= Math.max(MIN_SECTION_MS, (s.words / WORDS_PER_MIN) * 60000 * SECTION_READ_SHARE)) words += s.words
  }
  return words / answerWords
}

/** Sections seen twice or more, over sections seen at all. */
export function rereadRatio(sections: SectionStat[]): number {
  const seen = sections.filter((s) => s.visible_ms > 0)
  return seen.length ? seen.filter((s) => s.visits >= 2).length / seen.length : 0
}

/** 0-100 load score without the clarify follow-up term (that arrives later, server side). */
export function loadScoreBase(effort: number | null, reread: number, reversals: number, selections: number): number {
  const n = effort === null ? 0 : clamp((effort - 0.6) / 1.4, 0, 1)
  const x = 0.35 * n + 0.25 * reread + 0.15 * Math.min(reversals / 6, 1) + 0.15 * Math.min(selections / 4, 1)
  return Math.round(100 * clamp(x, 0, 1))
}

export type WordsBucket = "<200" | "200-500" | "500-1000" | "1000+"
export const wordsBucket = (words: number): WordsBucket =>
  words < 200 ? "<200" : words < 500 ? "200-500" : words < 1000 ? "500-1000" : "1000+"
